// Sign-in state for the app. The backend keeps the tokens in httpOnly
// cookies (an access token for 15 minutes, a refresh token for the 1-year
// session), so no script ever reads them: this module only tracks whether
// we're signed in, refreshes the access token before it runs out, and
// retries an API call once after a refresh when it comes back 401.
import { useSyncExternalStore } from "react";
import { API_BASE, apiUrl, backendUrlOf } from "./apiBase";

export type AuthStatus =
  | "loading"
  /** Signed in (or the backend has no sign-in yet: an older backend answers 404). */
  | "signed-in"
  | "signed-out"
  /** The backend has no AUTH_USERNAME / AUTH_PASSWORD. */
  | "not-configured";

export interface AuthState {
  status: AuthStatus;
  username: string | null;
  /** Why the last session ended, for the sign-in page ("Your session has expired…"). */
  notice: string | null;
}

let state: AuthState = { status: "loading", username: null, notice: null };
const listeners = new Set<() => void>();

function set(next: Partial<AuthState>) {
  state = { ...state, ...next };
  for (const fn of listeners) fn();
}

export const authStore = {
  get: () => state,
  subscribe(fn: () => void) {
    listeners.add(fn);
    return () => listeners.delete(fn);
  },
};

export function useAuth(): AuthState {
  return useSyncExternalStore(authStore.subscribe, authStore.get);
}

// The page's own fetch, before installAuthFetch wraps it.
const nativeFetch: typeof fetch = window.fetch.bind(window);

/** Refresh the access token now; concurrent callers share one request. */
let refreshing: Promise<boolean> | null = null;
let lastRefreshAt = 0;
/** False when the backend predates sign-in (its /api/auth answers 404): nothing to refresh. */
let authAvailable = true;

export function refreshSession(): Promise<boolean> {
  if (!authAvailable) return Promise.resolve(false);
  if (!refreshing) {
    refreshing = (async () => {
      try {
        const res = await nativeFetch(apiUrl("/api/auth/refresh"), { method: "POST", credentials: "include" });
        if (res.ok) {
          const json = await res.json().catch(() => ({}));
          lastRefreshAt = Date.now();
          set({ status: "signed-in", username: json.username ?? state.username, notice: null });
          return true;
        }
        const json = await res.json().catch(() => ({}));
        if (res.status === 503) set({ status: "not-configured", username: null });
        else if (state.status === "signed-in") set({ status: "signed-out", username: null, notice: json.error ?? "Please sign in again." });
        return false;
      } catch {
        return false; // offline: keep the current state, try again later
      } finally {
        refreshing = null;
      }
    })();
  }
  return refreshing;
}

/** First load: are we signed in? Tries a refresh before asking for the password. */
export async function loadSession(): Promise<void> {
  try {
    const res = await nativeFetch(apiUrl("/api/auth/session"), { credentials: "include" });
    if (res.status === 404) {
      // A backend from before sign-in existed: nothing to sign in to.
      authAvailable = false;
      set({ status: "signed-in", username: null });
      return;
    }
    authAvailable = true;
    const json = await res.json();
    if (!json.configured) {
      set({ status: "not-configured", username: null });
      return;
    }
    if (json.authenticated) {
      set({ status: "signed-in", username: json.username });
      return;
    }
    if (await refreshSession()) return;
    set({ status: "signed-out", username: null });
  } catch {
    // Backend unreachable: show the sign-in page, which says so when it tries.
    set({ status: "signed-out", username: null });
  }
}

export async function signIn(username: string, password: string): Promise<string | null> {
  try {
    const res = await nativeFetch(apiUrl("/api/auth/login"), {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ username, password }),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      if (res.status === 503) set({ status: "not-configured" });
      return json.error ?? "Sign-in failed. Try again.";
    }
    lastRefreshAt = Date.now();
    set({ status: "signed-in", username: json.username ?? username, notice: null });
    return null;
  } catch {
    return "Can't reach the backend. Is it running?";
  }
}

export async function signOut(): Promise<void> {
  try {
    await nativeFetch(apiUrl("/api/auth/logout"), { method: "POST", credentials: "include" });
  } finally {
    set({ status: "signed-out", username: null, notice: null });
  }
}


/** Refresh this often while signed in: the access token lasts 15 minutes. */
const KEEP_FRESH_MS = 10 * 60 * 1000;

/**
 * Wrap window.fetch once, at startup. Every API call ("/api/…") goes to the
 * backend (API_BASE in production) with the sign-in cookies; one answered
 * 401 refreshes the session and is sent again, and if the refresh fails the
 * app shows the sign-in page. Also refreshes every 10 minutes and when the
 * tab comes back, so the price WebSocket (which can't retry like this)
 * always has a valid token.
 */
export function installAuthFetch(): void {
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const backend = backendUrlOf(input);
    if (!backend) return nativeFetch(input, init);
    // Cookies go along to the backend's own name (Pages → api.…); same-origin it changes nothing.
    const send = (target: RequestInfo | URL) => nativeFetch(target, { ...init, credentials: "include" });
    // A Request's body can be read once: keep a copy for the retry.
    const target = (): RequestInfo | URL =>
      input instanceof Request ? new Request(backend.href, input.clone()) : backend.href;
    const res = await send(target());
    const guarded = !backend.pathname.startsWith("/api/auth/");
    if (res.status !== 401 || !guarded) return res;
    if (!(await refreshSession())) return res;
    return send(target());
  };

  const keepFresh = () => {
    if (authAvailable && state.status === "signed-in" && Date.now() - lastRefreshAt > KEEP_FRESH_MS) void refreshSession();
  };
  setInterval(keepFresh, 60 * 1000);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") keepFresh();
  });
}
